import type {ValueOrderDocument, ValueOrderDocumentList} from "@reva/shared/value-order";

import type ServiceApi from "../../api";
import {ErrorI18n} from "../../i18n";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {UploadDocumentResponse} from "../payload-types/documents";

/**
 * API interface to load the Value-Order-API documents of a valuation
 * (GET {@link RevaApi.PATH_VALUATIONS}/{id}/documents).
 *
 * The endpoint wraps the documents in an `{ items: [...] }` envelope; the interface unwraps and returns
 * the items, rejecting a malformed (non-array) response.
 */
export class DocumentsListApiInterface extends SimpleApiInterface<ValueOrderDocument[]> {
    constructor(private readonly revaId: string) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS, this.revaId, RevaApi.PATH_VALUATION_DOCUMENTS]);
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValueOrderDocument[]> {
        const response: ValueOrderDocumentList = await executor.fetchGET(url, credentials);
        if (!Array.isArray(response.items)) {
            console.error("Invalid response from Documents List:", response);
            throw new ErrorI18n("general.error.invalid");
        }
        return response.items;
    }

    protected handleApiErrors(e: Error): ValueOrderDocument[] {
        // Preserve the domain error thrown during response evaluation (malformed list) instead of
        // letting the base handler wrap it into "general.error.invalid".
        if (e instanceof ErrorI18n) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}

/**
 * API interface to download a single document of a valuation as a binary blob
 * (GET {@link RevaApi.PATH_VALUATIONS}/{id}/documents/{valueOrderGuid}/{documentId}.{extension}).
 */
export class DocumentDownloadApiInterface extends SimpleApiInterface<Blob> {
    constructor(
        private readonly revaId: string,
        private readonly valueOrderGuid: string,
        private readonly documentId: string,
        private readonly extension: string,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([
            RevaApi.PATH_VALUATIONS,
            this.revaId,
            RevaApi.PATH_VALUATION_DOCUMENTS,
            this.valueOrderGuid,
            `${this.documentId}.${this.extension}`,
        ]);
    }

    protected async executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<Blob> {
        return executor.fetchGETBinary(url, credentials);
    }
}

/**
 * API interface to upload a document to a valuation
 * (POST {@link RevaApi.PATH_VALUATIONS}/{id}/documents/{documentType}, multipart form data).
 */
export class DocumentUploadApiInterface extends SimpleApiInterface<UploadDocumentResponse> {
    constructor(
        private readonly revaId: string,
        private readonly documentType: string,
        private readonly document: File,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([
            RevaApi.PATH_VALUATIONS,
            this.revaId,
            RevaApi.PATH_VALUATION_DOCUMENTS,
            this.documentType,
        ]);
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<UploadDocumentResponse> {
        const fileData = new FormData();
        fileData.append("file", this.document, this.document.name);
        return executor.fetchPOSTFormData(url, credentials, fileData);
    }
}
